import React, { useEffect, useRef, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { useDispatch, useSelector } from 'react-redux';
import { searchNeevayNetworkByBusinessName } from 'actions/searchActions';
import searchService from 'services/searchService';
import businessService from 'services/businessService';
import BusinessInfoDrawer from './tableComponenets/BusinessInfoDrawer';

const MIN_SEARCH_LENGTH = 3;
const SUGGESTION_LIMIT = 8;

const getFirstValue = (value) => {
  if (Array.isArray(value)) return value.find(Boolean) || '';
  return value || '';
};

const toArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  return value ? [value] : [];
};

const getClientDisplayName = (client) => {
  if (typeof client === 'string' || typeof client === 'number') return String(client).trim();
  if (!client || typeof client !== 'object') return '';

  return String(
    client.clientName ||
    client.companyName ||
    client.businessName ||
    client.entityName ||
    client.name ||
    client.title ||
    ''
  ).trim();
};

const normalizeClientNames = (clients) => (
  toArray(clients).map(getClientDisplayName).filter(Boolean)
);

const getPrimaryAddress = (profile = {}, payload = {}) => {
  const addresses = profile.addresses || payload.addresses || profile.businessProfile?.addresses || [];
  return Array.isArray(addresses) ? addresses.find(Boolean) || {} : addresses || {};
};

export const mapProfileToDrawerData = (profile = {}, payload = {}) => {
  const businessProfile = profile.businessProfile || {};
  const statutory = profile.statutory || payload.statutory || {};
  const address = getPrimaryAddress(profile, payload);
  const services = Array.isArray(profile.services) ? profile.services : [];
  const serviceLocations = services.flatMap((service) => service?.locations || []);
  const clients = payload?.clients?.clients || payload?.clients || profile.clients || [];
  const projects = payload?.projects?.projects || payload?.projects || profile.projects || [];
  const clientNames = normalizeClientNames(clients);
  const regionStates = [
    ...toArray(profile.state),
    ...toArray(profile.regions?.state),
    ...serviceLocations.flatMap((location) => typeof location === 'string' ? [] : toArray(location?.state || location?.states)),
  ];
  const regionCities = [
    ...toArray(profile.selectedcities),
    ...toArray(profile.selectedCities),
    ...toArray(profile.regions?.selectedcities),
    ...toArray(profile.regions?.selectedCities),
    ...serviceLocations.flatMap((location) => typeof location === 'string' ? [location] : toArray(location?.selectedcities || location?.selectedCities || location?.city || location?.cities)),
  ];

  return {
    ...profile,
    hightlights:
      businessProfile.businessHeadline ||
      profile.businessHeadline ||
      profile.hightlights ||
      profile.headline,
    description: businessProfile.description || profile.description,
    establishYear: businessProfile.establishYear || profile.establishYear,
    addresses_line1: address.addressLine1 || profile.addresses_line1,
    addresses_line2: address.addressLine2 || profile.addresses_line2,
    addresses_city: address.city || profile.addresses_city,
    addresses_district: address.district || profile.addresses_district,
    addresses_state: address.state || profile.addresses_state,
    addresses_country: address.country || profile.addresses_country,
    businessWebsite: businessProfile.websiteUrl || profile.websiteUrl || profile.businessWebsite,
    gstNumber: statutory.gstin || statutory.gstNumber || businessProfile.gstin || businessProfile.gstNumber || profile.gstNumber || profile.gstin || profile.gstIn,
    businessMobileNumber:
      businessProfile.businessMobile ||
      profile.businessMobile ||
      profile.businessMobileNumber ||
      profile.contact,
    contact:
      businessProfile.contact ||
      businessProfile.businessMobile ||
      profile.contact ||
      profile.businessMobile ||
      profile.businessMobileNumber,
    businessEmail: businessProfile.businessEmail || profile.businessEmail,
    businessCategory: businessProfile.businessCategory || profile.businessCategory,
    businessType: businessProfile.businessType || profile.businessType,
    owners: profile.owners || payload?.owners || [],
    clients,
    clientNames: clientNames.length ? clientNames : normalizeClientNames(profile.clientNames),
    isoCertificates: businessProfile.certificates || profile.isoCertificates || profile.certificates || [],
    projectNames: Array.isArray(projects)
      ? projects.map((project) => project?.projectName || project?.name || project).filter(Boolean)
      : [],
    projects,
    country: profile.country || profile.regions?.country || [],
    state: [...new Set(regionStates.filter(Boolean))],
    selectedcities: [...new Set(regionCities.filter(Boolean))],
    serviceNames: services.map((service) => service?.serviceName).filter(Boolean),
    serviceDetails: services.flatMap((service) => service?.serviceDetails || []).filter(Boolean).join(', '),
    vendorCategories: profile.vendorCategories || businessProfile.vendorCategories || [],
  };
};

const normalizeSuggestion = (item = {}) => {
  const addressParts = [
    getFirstValue(item['businessProfile.addresses.addressLine1']),
    getFirstValue(item['businessProfile.addresses.addressLine2']),
    getFirstValue(item['businessProfile.addresses.city']),
    getFirstValue(item['businessProfile.addresses.state']),
  ].filter(Boolean);

  return {
    supplierId: getFirstValue(item.supplierId),
    businessName: getFirstValue(item.businessName),
    address: addressParts.join(', '),
    category: getFirstValue(item['businessProfile.businessCategory']),
    isPremium: String(getFirstValue(item.subscriptionType)).toUpperCase() === 'PREMIUM',
  };
};

const BusinessNameSearchBar = ({ vendorType = '', selectionMode = false, selectedVendors = [], onSelectionChange }) => {
  const dispatch = useDispatch();
  const isSearching = useSelector((state) => state.search?.isSearching);
  const [businessName, setBusinessName] = useState('');
  const [showError, setShowError] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSuggestionLoading, setIsSuggestionLoading] = useState(false);
  const [openBusinessInfoDrawer, setOpenBusinessInfoDrawer] = useState(false);
  const [businessInfo, setBusinessInfo] = useState({});
  const previousVendorType = useRef(vendorType);
  const wrapperRef = useRef(null);
  const selectedSupplierIds = new Set(
    (selectedVendors || []).map((vendor) => String(getFirstValue(vendor?.supplierId))).filter(Boolean),
  );

  const buildSelectableVendor = (suggestion = {}, payload = {}) => {
    const mappedProfile = payload?.profile
      ? mapProfileToDrawerData(payload.profile, payload)
      : {};
    const businessNameValue = mappedProfile.businessName || suggestion.businessName;
    const businessEmail = mappedProfile.businessEmail || suggestion.businessEmail || '';
    const businessMobile =
      mappedProfile.businessMobileNumber ||
      mappedProfile.businessMobile ||
      mappedProfile.contact ||
      suggestion.businessMobile ||
      '';
    const contactDetails = [
      ...(Array.isArray(mappedProfile.contactDetails) ? mappedProfile.contactDetails : []),
      {
        name: 'Primary',
        mobile: businessMobile,
        email: businessEmail,
      },
    ].filter((contact) => contact.email || contact.mobile);

    return {
      ...suggestion,
      ...mappedProfile,
      source: 'MARKETPLACE',
      supplierId: mappedProfile.supplierId || suggestion.supplierId,
      businessName: Array.isArray(businessNameValue)
        ? businessNameValue.join(', ')
        : businessNameValue || suggestion.businessName,
      businessCategory: mappedProfile.businessCategory || suggestion.category,
      businessEmail,
      businessMobile,
      businessMobileNumber: businessMobile,
      contactDetails,
      owners: Array.isArray(mappedProfile.owners) ? mappedProfile.owners : [],
      subscriptionType: mappedProfile.subscriptionType || (suggestion.isPremium ? 'PREMIUM' : ''),
      isPremium: Boolean(suggestion.isPremium || mappedProfile.subscriptionType === 'PREMIUM'),
      location: mappedProfile.location || { address: suggestion.address },
      vendorCategories: mappedProfile.vendorCategories || [],
    };
  };

  const toggleSuggestionSelection = async (suggestion = {}) => {
    if (!selectionMode || typeof onSelectionChange !== 'function') return;

    const supplierId = String(suggestion.supplierId || '');

    if (selectedSupplierIds.has(supplierId)) {
      onSelectionChange(
        (selectedVendors || []).filter((vendor) => String(getFirstValue(vendor?.supplierId)) !== supplierId),
      );
      return;
    }

    let selectableVendor = buildSelectableVendor(suggestion);

    if (suggestion.supplierId) {
      try {
        const payload = await businessService.fetchBusinessById(suggestion.supplierId);
        selectableVendor = buildSelectableVendor(suggestion, payload);
      } catch (error) {
        console.error('Failed to fetch marketplace vendor contact details', error);
      }
    }

    onSelectionChange([...(selectedVendors || []), selectableVendor]);
  };
  const runBusinessSearch = (name) => {
    const trimmedBusinessName = name.trim();
    if (!trimmedBusinessName) {
      setShowError(true);
      return;
    }

    setShowError(false);
    setShowSuggestions(false);
    dispatch(
      searchNeevayNetworkByBusinessName({
        businessName: trimmedBusinessName,
        vendorType,
      }),
    );
  };

  const handleSearch = (event) => {
    event.preventDefault();
    runBusinessSearch(businessName);
  };

  const handleBusinessInfoDrawerClose = () => {
    setOpenBusinessInfoDrawer(false);
    setBusinessInfo({});
  };

  const buildSuggestionDrawerData = (suggestion, payload = {}) => {
    const profile = payload?.profile || {};
    const mappedProfile = mapProfileToDrawerData(profile, payload);
    const businessNameValue = mappedProfile.businessName || suggestion.businessName;

    return {
      ...mappedProfile,
      supplierId: mappedProfile.supplierId || suggestion.supplierId,
      businessName: Array.isArray(businessNameValue)
        ? businessNameValue
        : [businessNameValue].filter(Boolean),
      subscriptionType:
        mappedProfile.subscriptionType || (suggestion.isPremium ? 'PREMIUM' : 'BASIC'),
      vendorCategories: mappedProfile.vendorCategories || [],
      projects: mappedProfile.projects || [],
    };
  };

  const handleSuggestionSelect = async (suggestion) => {
    if (selectionMode) {
      toggleSuggestionSelection(suggestion);
      return;
    }
    setBusinessName(suggestion.businessName);
    setShowSuggestions(false);

    const baseDrawerData = buildSuggestionDrawerData(suggestion);
    setBusinessInfo(baseDrawerData);
    setOpenBusinessInfoDrawer(true);

    if (!suggestion.supplierId) return;

    try {
      const payload = await businessService.fetchBusinessById(suggestion.supplierId);
      setBusinessInfo((previousInfo) => ({
        ...previousInfo,
        ...buildSuggestionDrawerData(suggestion, payload),
      }));
    } catch (error) {
      console.error('Failed to fetch marketplace vendor details', error);
    }
  };

  useEffect(() => {
    if (previousVendorType.current === vendorType) return;
    previousVendorType.current = vendorType;

    const trimmedBusinessName = businessName.trim();
    if (!trimmedBusinessName) return;

    dispatch(
      searchNeevayNetworkByBusinessName({
        businessName: trimmedBusinessName,
        vendorType,
      }),
    );
  }, [businessName, dispatch, vendorType]);

  useEffect(() => {
    const trimmedBusinessName = businessName.trim();
    if (trimmedBusinessName.length < MIN_SEARCH_LENGTH) {
      setSuggestions([]);
      setShowSuggestions(false);
      setIsSuggestionLoading(false);
      return undefined;
    }

    let isActive = true;
    setIsSuggestionLoading(true);

    const timerId = setTimeout(async () => {
      try {
        const docs = await searchService.searchBusinessNameSuggestions(trimmedBusinessName);
        if (!isActive) return;

        const mappedSuggestions = docs
          .map(normalizeSuggestion)
          .filter((suggestion) => suggestion.businessName)
          .slice(0, SUGGESTION_LIMIT);

        setSuggestions(mappedSuggestions);
        setShowSuggestions(true);
      } catch (error) {
        if (!isActive) return;
        setSuggestions([]);
        setShowSuggestions(false);
      } finally {
        if (isActive) setIsSuggestionLoading(false);
      }
    }, 300);

    return () => {
      isActive = false;
      clearTimeout(timerId);
    };
  }, [businessName]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <>
      <form ref={wrapperRef} className='relative min-w-0' onSubmit={handleSearch}>
            <div
        className={`flex h-[42px] min-w-0 items-center rounded-md border bg-white/95 px-3 shadow-sm transition ${
          showError
            ? 'border-[#EB5A4F] ring-2 ring-red-100'
            : 'border-[#F0D8CA] focus-within:border-[#E97A3A] focus-within:bg-white'
        }`}
      >
        <button
          type='submit'
          aria-label='Search by business name'
          disabled={isSearching}
          className='mr-2 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#FFF4EE] text-[#C66537] transition hover:bg-[#FFE8DA] hover:text-[#A94316] disabled:cursor-not-allowed disabled:opacity-60'
        >
          <SearchIcon className='h-[18px] w-[18px]' />
        </button>
        <input
          type='text'
          value={businessName}
          onChange={(event) => {
            setBusinessName(event.target.value);
            if (showError) setShowError(false);
          }}
          onFocus={() => {
            if (suggestions.length || businessName.trim().length >= MIN_SEARCH_LENGTH) {
              setShowSuggestions(true);
            }
          }}
          placeholder='Search by business name'
          className='h-7 min-w-0 flex-1 border-none bg-transparent px-0 text-xs font-medium text-[#303026] placeholder:text-[#8B90A0] focus:ring-transparent'
        />
      </div>

      {showError && (
        <p className='absolute left-1 top-[46px] z-40 text-[11px] font-medium text-red-600'>
          Please enter a business name.
        </p>
      )}

      {showSuggestions && businessName.trim().length >= MIN_SEARCH_LENGTH && (
        <div className='absolute left-0 top-[48px] z-50 w-[min(560px,calc(100vw-48px))] overflow-hidden rounded-xl border border-[#F1D7C8] bg-white shadow-[0_18px_50px_rgba(31,30,41,0.16)]'>
          <div className='border-b border-[#F8E5DA] bg-[#FFF8F3] px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-[#B43A12]'>
            Matching businesses
          </div>

          {isSuggestionLoading ? (
            <div className='px-3 py-4 text-xs font-medium text-[#7A8597]'>
              Searching businesses...
            </div>
          ) : suggestions.length ? (
            <div className='max-h-[320px] overflow-y-auto py-1'>
              {suggestions.map((suggestion) => {
                const isSelected = selectedSupplierIds.has(String(suggestion.supplierId || ''));

                return (
                  <button
                    key={`${suggestion.supplierId}-${suggestion.businessName}`}
                    type='button'
                    onClick={() => handleSuggestionSelect(suggestion)}
                    className={`group w-full border-b border-[#F7EEE8] px-3 py-2.5 text-left transition last:border-b-0 hover:bg-[#FFF4EE] ${isSelected ? 'bg-[#FFF4EE]' : ''}`}
                  >
                    <div className='flex items-start justify-between gap-3'>
                      {selectionMode && (
                        <span
                          className={`mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border text-[10px] font-bold ${isSelected ? 'border-[#FD6600] bg-[#FD6600] text-white' : 'border-[#D6D8DF] bg-white text-transparent'}`}
                        >
                          {isSelected ? "\u2713" : ""}
                        </span>
                      )}
                      <div className='min-w-0 flex-1'>
                        <div className='flex min-w-0 items-center'>
                          <p className='truncate text-sm font-semibold text-[#22221A] group-hover:text-[#B43A12]'>
                            {suggestion.businessName}
                          </p>
                          {suggestion.isPremium && (
                            <img
                              src='https://storagereponeevaydevcdn.blob.core.windows.net/business/Verified_image_nn.png'
                              alt='verified'
                              className='ml-1 h-[18px] w-[18px] shrink-0'
                            />
                          )}
                        </div>
                        <p className='mt-0.5 line-clamp-1 text-[11px] font-medium text-[#7A8597]'>
                          {suggestion.address || 'Address not available'}
                        </p>
                      </div>
                      <span className='shrink-0 rounded-full bg-[#F8F8F2] px-2.5 py-1 text-[10px] font-bold text-[#555548]'>
                        {suggestion.category || 'Category NA'}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className='px-3 py-4 text-xs font-medium text-[#7A8597]'>
              No matching businesses found.
            </div>
          )}
        </div>
      )}
      </form>
      <BusinessInfoDrawer
        handleOpenBusinessInfoDrawerClose={handleBusinessInfoDrawerClose}
        values={businessInfo}
        openBusinessInfoDrawer={openBusinessInfoDrawer}
      />
    </>
  );
};

export default BusinessNameSearchBar;












